import React, {useMemo} from 'react'
import {
  CollapsiblePermissionGroups,
  type PermissionBulkCheckboxConfig,
  type PermissionGlobalBulkConfig,
  type PermissionOverrideModuleBulkConfig,
} from './CollapsiblePermissionGroups'
import {ProductionGroupedPermissionAccordion} from './ProductionGroupedPermissionAccordion'
import {
  groupPermissionCatalogForProductionUI,
  assignProductionPermissionPlacement,
  productionModuleOrderIndex,
} from '../core/productionPermissionGrouping'
import {
  resolveCatalogEntry,
  type PermissionCatalogEntry,
} from '../core/permissionGrouping'
import type {
  AccessGrantEffect,
  AccessScope,
  EffectivePermissionDecision,
} from '../core/accessRequests'
import SearchableSelect from '../../../shared/components/SearchableSelect'

const norm = (c: string) => c.trim().toUpperCase()

export type BundleMappedRow = {
  permissionCode: string
  effect: AccessGrantEffect
  defaultScope?: AccessScope | null
}

export type PreviewCatalogRow = PermissionCatalogEntry & {
  decision: EffectivePermissionDecision
}

/** Role matrix & create-role manual step: one checkbox per catalog row */
export function GroupedPermissionCheckboxGrid({
  catalog,
  searchQuery,
  selectedCodes,
  disabled,
  onToggle,
  emptyMessage = 'No permissions match.',
  countLegend = 'selected',
  bulk,
  startAllExpanded = false,
}: {
  catalog: PermissionCatalogEntry[]
  searchQuery: string
  selectedCodes: Set<string>
  disabled: boolean
  onToggle: (code: string) => void
  emptyMessage?: string
  /** Shown in group header: "{on} / {total} {countLegend}" when bulk selection is off */
  countLegend?: string
  bulk?: {
    totalCatalogCount: number
    onSetMany: (codes: string[], selected: boolean) => void
    disabled?: boolean
    /** Global count label after the fraction, e.g. "on role", "selected" */
    globalLegend?: string
  }
  /** When true and search is empty, all groups start expanded (full matrix view). */
  startAllExpanded?: boolean
}) {
  const bulkCheckbox: PermissionBulkCheckboxConfig<PermissionCatalogEntry> | undefined = bulk
    ? {
        getItemCode: (e) => norm(resolveCatalogEntry(e).permissionCode),
        isSelected: (code) => selectedCodes.has(code),
        onSetMany: bulk.onSetMany,
        disabled: bulk.disabled ?? disabled,
        moduleCountLegend: countLegend,
      }
    : undefined
  const globalBulk: PermissionGlobalBulkConfig | undefined = bulk
    ? {
        totalCatalogCount: bulk.totalCatalogCount,
        selectedGlobalCount: selectedCodes.size,
        legend: bulk.globalLegend ?? countLegend,
        disabled: bulk.disabled ?? disabled,
      }
    : undefined
  return (
    <ProductionGroupedPermissionAccordion
      catalog={catalog}
      searchQuery={searchQuery}
      getItemKey={(e) => resolveCatalogEntry(e).permissionCode}
      emptyMessage={emptyMessage}
      startAllExpanded={startAllExpanded}
      bulkCheckbox={bulkCheckbox}
      globalBulk={globalBulk}
      renderItem={(entry) => {
        const r = resolveCatalogEntry(entry)
        const checked = selectedCodes.has(norm(r.permissionCode))
        return (
          <div className='d-flex align-items-start gap-2 border-top bg-white perm-matrix-row'>
            <div
              className='d-flex justify-content-center flex-shrink-0 pt-2'
              style={{width: '2.5rem', minWidth: '2.5rem'}}
            >
              <input
                type='checkbox'
                className='form-check-input flex-shrink-0'
                style={{marginTop: 0, width: '1.05rem', height: '1.05rem'}}
                checked={checked}
                disabled={disabled}
                onChange={() => onToggle(r.permissionCode)}
                id={`perm-cb-${r.permissionCode}`}
              />
            </div>
            <label
              className='mb-0 flex-grow-1 py-2 pe-3'
              htmlFor={`perm-cb-${r.permissionCode}`}
              style={{cursor: disabled ? 'default' : 'pointer', minWidth: 0}}
            >
              <div className='fw-medium text-gray-900 small'>{r.displayName}</div>
              <div className='font-monospace text-muted' style={{fontSize: '0.72rem'}}>{r.permissionCode}</div>
              {entry.description?.trim() ? (
                <div className='small text-gray-600 mt-1'>{entry.description}</div>
              ) : null}
            </label>
          </div>
        )
      }}
    />
  )
}

/** Permission catalog (read-only reference) */
export function GroupedPermissionCatalogReadonly({
  catalog,
  searchQuery,
}: {
  catalog: PermissionCatalogEntry[]
  searchQuery: string
}) {
  return (
    <ProductionGroupedPermissionAccordion
      catalog={catalog}
      searchQuery={searchQuery}
      getItemKey={(e) => resolveCatalogEntry(e).permissionCode}
      emptyMessage='No permissions match your search.'
      renderItem={(entry) => {
        const r = resolveCatalogEntry(entry)
        return (
          <div className='d-flex align-items-start border-top px-3 py-1 bg-white'>
            <div className='flex-grow-1'>
              <div className='fw-semibold text-gray-900 small'>{r.displayName}</div>
              <div className='font-monospace text-muted' style={{fontSize: '0.72rem'}}>{r.permissionCode}</div>
              {entry.description?.trim() ? (
                <div className='small text-gray-700 mt-1'>{entry.description}</div>
              ) : null}
            </div>
            <span className='badge badge-light-info text-gray-800 align-self-start'>Reference</span>
          </div>
        )
      }}
    />
  )
}

/** Package setup: checkbox + effect + default scope */
export function GroupedBundlePermissionEditor({
  catalog,
  searchQuery,
  getMapped,
  selectedBundleOk,
  systemLocked,
  onToggleInBundle,
  onEffect,
  onScope,
  /** When scoped to “in this package only”, every row is in the package — header counts reflect that. */
  packageCountMode = 'full',
  emptyMessage = 'No permissions match.',
  packageBulk,
}: {
  catalog: PermissionCatalogEntry[]
  searchQuery: string
  getMapped: (code: string) => BundleMappedRow | undefined
  selectedBundleOk: boolean
  systemLocked: boolean
  onToggleInBundle: (code: string, enabled: boolean) => void
  onEffect: (code: string, effect: AccessGrantEffect) => void
  onScope: (code: string, scope: '' | AccessScope) => void
  packageCountMode?: 'full' | 'scoped'
  emptyMessage?: string
  packageBulk?: {
    totalCatalogCount: number
    selectedCount: number
    onSetManyInPackage: (codes: string[], inPackage: boolean) => void
    disabled?: boolean
  }
}) {
  void packageCountMode
  const disabled = !selectedBundleOk || systemLocked
  const bulkCheckbox: PermissionBulkCheckboxConfig<PermissionCatalogEntry> | undefined = packageBulk
    ? {
        getItemCode: (e) => norm(resolveCatalogEntry(e).permissionCode),
        isSelected: (code) => Boolean(getMapped(code)),
        onSetMany: packageBulk.onSetManyInPackage,
        disabled: packageBulk.disabled ?? disabled,
        moduleCountLegend: 'in package',
      }
    : undefined
  const globalBulk: PermissionGlobalBulkConfig | undefined = packageBulk
    ? {
        totalCatalogCount: packageBulk.totalCatalogCount,
        selectedGlobalCount: packageBulk.selectedCount,
        legend: 'in package',
        disabled: packageBulk.disabled ?? disabled,
      }
    : undefined
  return (
    <ProductionGroupedPermissionAccordion
      catalog={catalog}
      searchQuery={searchQuery}
      getItemKey={(e) => resolveCatalogEntry(e).permissionCode}
      emptyMessage={emptyMessage}
      bulkCheckbox={bulkCheckbox}
      globalBulk={globalBulk}
      renderItem={(entry) => {
        const r = resolveCatalogEntry(entry)
        const existing = getMapped(r.permissionCode)
        return (
          <div className='d-flex flex-wrap align-items-center gap-1 gap-md-2 border-top px-2 py-1 bg-white'>
            <div className='d-flex align-items-center flex-shrink-0' style={{width: '32px'}}>
              <input
                type='checkbox'
                className='form-check-input m-0'
                checked={Boolean(existing)}
                disabled={disabled}
                onChange={(e) => onToggleInBundle(r.permissionCode, e.target.checked)}
              />
            </div>
            <div className='flex-grow-1' style={{minWidth: '160px'}}>
              <div className='fw-medium text-gray-900 small'>{r.displayName}</div>
              <div className='font-monospace text-muted' style={{fontSize: '0.72rem'}}>{r.permissionCode}</div>
            </div>
            <SearchableSelect
              className='form-select form-select-sm flex-shrink-0'
              style={{width: '108px'}}
              disabled={!existing || disabled}
              value={existing?.effect || 'ALLOW'}
              onChange={(e) => onEffect(r.permissionCode, e.target.value as AccessGrantEffect)}
            >
              <option value='ALLOW'>ALLOW</option>
              <option value='DENY'>DENY</option>
            </SearchableSelect>
            <SearchableSelect
              className='form-select form-select-sm flex-shrink-0'
              style={{width: '118px'}}
              disabled={!existing || disabled}
              value={existing?.defaultScope || ''}
              onChange={(e) => onScope(r.permissionCode, e.target.value as '' | AccessScope)}
            >
              <option value=''>None</option>
              <option value='ALL'>ALL</option>
              <option value='COMPANY'>COMPANY</option>
              <option value='VESSEL'>VESSEL</option>
            </SearchableSelect>
          </div>
        )
      }}
    />
  )
}

/** Final access preview */
export function GroupedEffectiveAccessPanel({
  rows,
  searchQuery,
}: {
  rows: PreviewCatalogRow[]
  searchQuery: string
}) {
  return (
    <ProductionGroupedPermissionAccordion
      catalog={rows}
      searchQuery={searchQuery}
      extraSearch={(r, qu) => {
        const d = r.decision
        const trail = (d.sourceTrail || []).join(' ').toUpperCase()
        const tag = (d.sourceTag || '').toUpperCase()
        const scope = (d.scope || '').toUpperCase()
        const res = d.allowed ? 'ALLOWED' : 'NOT ALLOWED'
        return trail.includes(qu) || tag.includes(qu) || scope.includes(qu) || res.includes(qu)
      }}
      getItemKey={(r) => r.permissionCode}
      emptyMessage='No permissions match your search.'
      renderItem={(row) => {
        const r = resolveCatalogEntry(row)
        const d = row.decision
        return (
          <div className='d-flex flex-wrap align-items-start gap-2 border-top px-3 py-1 bg-white'>
            <div className='flex-grow-1' style={{minWidth: '200px'}}>
              <div className='fw-medium text-gray-900 small'>{r.displayName}</div>
              <div className='font-monospace text-muted' style={{fontSize: '0.72rem'}}>{r.permissionCode}</div>
            </div>
            <div>
              <span className={`badge ${d.allowed ? 'badge-light-success' : 'badge-light-danger'}`}>
                {d.allowed ? 'Allowed' : 'Not allowed'}
              </span>
            </div>
            <div className='small text-gray-800' style={{minWidth: '72px'}}>
              {d.scope || '—'}
            </div>
            <div className='small text-gray-800' style={{minWidth: '100px'}}>
              {d.sourceTag || '—'}
            </div>
            <div className='text-break small text-gray-700 flex-grow-1' style={{minWidth: '140px'}}>
              {(d.sourceTrail || []).join(' → ') || '—'}
            </div>
          </div>
        )
      }}
    />
  )
}

export type OverrideTableRow = {
  index: number
  permissionCode: string
  effect: AccessGrantEffect
  scope: AccessScope | null
  reason: string
}

type OverrideItem = OverrideTableRow & {entry: PermissionCatalogEntry}

function buildOverrideGroups(
  rows: OverrideTableRow[],
  catalogByCode: Map<string, PermissionCatalogEntry>,
  placementCatalog: PermissionCatalogEntry[],
  searchQuery: string
): {moduleKey: string; items: OverrideItem[]}[] {
  const q = searchQuery.trim().toUpperCase()
  let list: OverrideItem[] = rows.map((row) => {
    const code = norm(row.permissionCode || '')
    const raw = row.permissionCode?.trim() ? catalogByCode.get(code) : undefined
    const entry =
      raw ||
      ({
        permissionId: -1,
        permissionCode: code || '',
        displayName: null,
        moduleCategory: null,
        description: null,
        sortOrder: null,
      } as PermissionCatalogEntry)
    return {...row, entry}
  })
  if (q) {
    list = list.filter((it) => {
      const r = resolveCatalogEntry(it.entry)
      const codeU = (it.permissionCode || '').toUpperCase()
      const pl = assignProductionPermissionPlacement(it.entry, placementCatalog)
      const placementTxt = `${pl.moduleLabel} ${pl.menuLabel} ${pl.subMenuLabel}`.toUpperCase()
      return (
        codeU.includes(q) ||
        (r.displayName || '').toUpperCase().includes(q) ||
        (r.description || '').toUpperCase().includes(q) ||
        (r.moduleCategory || '').toUpperCase().includes(q) ||
        placementTxt.includes(q)
      )
    })
  }
  type BucketMeta = {items: OverrideItem[]; pl: ReturnType<typeof assignProductionPermissionPlacement>}
  const byBucket = new Map<string, BucketMeta>()
  for (const it of list) {
    const pl = assignProductionPermissionPlacement(it.entry, placementCatalog)
    const moduleKey =
      pl.subMenuLabel && pl.subMenuLabel !== '—' ?
        `${pl.moduleLabel} · ${pl.menuLabel} · ${pl.subMenuLabel}`
      : `${pl.moduleLabel} · ${pl.menuLabel}`
    if (!byBucket.has(moduleKey)) {
      byBucket.set(moduleKey, {items: [], pl})
    }
    byBucket.get(moduleKey)!.items.push(it)
  }
  for (const bm of Array.from(byBucket.values())) {
    bm.items.sort((a: OverrideItem, b: OverrideItem) => {
      const da = (resolveCatalogEntry(a.entry).displayName || '').toLowerCase()
      const db = (resolveCatalogEntry(b.entry).displayName || '').toLowerCase()
      if (da !== db) return da.localeCompare(db)
      return norm(a.permissionCode).localeCompare(norm(b.permissionCode))
    })
  }
  const entries = Array.from(byBucket.entries()).sort(([ka, ma], [kb, mb]) => {
    const cmpMod = productionModuleOrderIndex(ma.pl.moduleLabel) - productionModuleOrderIndex(mb.pl.moduleLabel)
    if (cmpMod !== 0) return cmpMod
    if (ma.pl.sortIndex !== mb.pl.sortIndex) return ma.pl.sortIndex - mb.pl.sortIndex
    return ka.localeCompare(kb)
  })
  return entries.map(([moduleKey, bm]) => ({moduleKey, items: bm.items}))
}

/** Grouped advanced override editor rows */
export function GroupedUserOverrideEditor({
  rows,
  catalogEntries,
  catalogByCode,
  searchQuery,
  advancedMode,
  onPermissionChange,
  onEffectChange,
  onScopeChange,
  onReasonChange,
  onRemove,
  overrideModuleBulk,
}: {
  rows: OverrideTableRow[]
  /** Full catalog for stable optgroup ordering */
  catalogEntries: PermissionCatalogEntry[]
  catalogByCode: Map<string, PermissionCatalogEntry>
  searchQuery: string
  advancedMode: boolean
  onPermissionChange: (index: number, code: string) => void
  onEffectChange: (index: number, effect: AccessGrantEffect) => void
  onScopeChange: (index: number, scope: AccessScope | null) => void
  onReasonChange: (index: number, reason: string) => void
  onRemove: (index: number) => void
  overrideModuleBulk?: PermissionOverrideModuleBulkConfig<OverrideItem>
}) {
  const fullCatalogGroups = useMemo(
    () => groupPermissionCatalogForProductionUI(catalogEntries, {searchQuery: ''}),
    [catalogEntries]
  )

  const groups = useMemo(
    () => buildOverrideGroups(rows, catalogByCode, catalogEntries, searchQuery),
    [rows, catalogByCode, catalogEntries, searchQuery]
  )

  if (rows.length === 0) {
    return (
      <div className='text-center text-gray-700 py-6 border rounded bg-light'>
        No overrides yet. Use Simple builder or add a row.
      </div>
    )
  }

  return (
    <CollapsiblePermissionGroups
      groups={groups}
      searchQuery={searchQuery}
      getItemKey={(it) => `ov-${it.index}-${it.permissionCode}`}
      emptyMessage='No overrides match your search.'
      overrideModuleBulk={overrideModuleBulk}
      renderGroupHeaderRight={
        overrideModuleBulk
          ? undefined
          : (_, gItems) => {
              const allow = gItems.filter((r) => r.effect === 'ALLOW').length
              const deny = gItems.filter((r) => r.effect === 'DENY').length
              return (
                <span className='text-muted'>
                  {gItems.length} override{gItems.length === 1 ? '' : 's'} · {allow} allow · {deny} deny
                </span>
              )
            }
      }
      renderItem={(item) => (
        <div className='d-flex flex-wrap align-items-center gap-1 border-top px-2 py-1 bg-white'>
          <div className='flex-grow-1' style={{minWidth: '200px'}}>
            {advancedMode ? (
              <SearchableSelect
                className='form-select form-select-sm'
                value={item.permissionCode}
                onChange={(e) => onPermissionChange(item.index, e.target.value)}
              >
                <option value=''>Select permission</option>
                {fullCatalogGroups.map((section) => (
                  <optgroup key={section.moduleKey} label={section.moduleKey}>
                    {section.items.map((e) => {
                      const r = resolveCatalogEntry(e)
                      return (
                        <option key={r.permissionCode} value={r.permissionCode}>
                          {r.displayName} · {r.permissionCode}
                        </option>
                      )
                    })}
                  </optgroup>
                ))}
              </SearchableSelect>
            ) : (
              <>
                <span className='fw-semibold text-gray-900'>{resolveCatalogEntry(item.entry).displayName}</span>
                <div className='font-monospace small text-muted'>{item.permissionCode || '—'}</div>
              </>
            )}
          </div>
          <SearchableSelect
            className='form-select form-select-sm'
            style={{width: '110px'}}
            value={item.effect}
            onChange={(e) => onEffectChange(item.index, e.target.value as AccessGrantEffect)}
          >
            <option value='ALLOW'>ALLOW</option>
            <option value='DENY'>DENY</option>
          </SearchableSelect>
          <SearchableSelect
            className='form-select form-select-sm'
            style={{width: '120px'}}
            value={item.scope || ''}
            onChange={(e) =>
              onScopeChange(item.index, (e.target.value || null) as AccessScope | null)
            }
          >
            <option value=''>None</option>
            <option value='ALL'>ALL</option>
            <option value='COMPANY'>COMPANY</option>
            <option value='VESSEL'>VESSEL</option>
          </SearchableSelect>
          <input
            className='form-control form-control-sm flex-grow-1'
            style={{minWidth: '140px'}}
            value={item.reason || ''}
            onChange={(e) => onReasonChange(item.index, e.target.value)}
          />
          <button type='button' className='btn btn-sm btn-light-danger' onClick={() => onRemove(item.index)}>
            Remove
          </button>
        </div>
      )}
    />
  )
}
